import { useState, useRef, useCallback } from "react";

const API = window.location.origin + "/api/v1";
const NAVY = "#0A0F1E";
const GOLD = "#D4AF37";
const GREEN = "#059669";
const RED = "#EF4444";
const INDIGO = "#4F46E5";
const MUTED = "rgba(255,255,255,0.4)";
const BORDER = "rgba(255,255,255,0.08)";

function fmt(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export default function MixerPanel() {
  const [sessionId] = useState(() => "mixer_" + Date.now());
  const [clips, setClips] = useState([]); // [{ name, size, duration, uploaded }]
  const [audioFile, setAudioFile] = useState(null); // WAV from soundboard
  const [audioUploaded, setAudioUploaded] = useState(false);
  const [outputName, setOutputName] = useState("episode_render");
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null); // queued|rendering|complete|failed
  const [jobError, setJobError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [renderStatus, setRenderStatus] = useState("");
  const pollRef = useRef(null);
  const videoInputRef = useRef(null);
  const audioInputRef = useRef(null);

  // ── Upload a video clip ──────────────────────────────────────────
  const uploadClip = async (file) => {
    const form = new FormData();
    form.append("session_id", sessionId);
    form.append("file", file);
    const r = await fetch(`${API}/episode-mixer/upload-clip`, { method: "POST", body: form });
    if (!r.ok) throw new Error(await r.text());
    return await r.json();
  };

  const handleVideoFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    setRenderStatus("Uploading clips...");
    try {
      const newClips = [];
      for (const file of Array.from(files)) {
        const res = await uploadClip(file);
        // Get duration via video element
        const dur = await getVideoDuration(file);
        newClips.push({ name: res.filename, size: file.size, duration: dur, uploaded: true });
      }
      setClips(prev => [...prev, ...newClips]);
      setRenderStatus(`✓ ${newClips.length} clip${newClips.length > 1 ? "s" : ""} uploaded`);
    } catch (e) {
      setRenderStatus("Upload failed: " + e.message);
    }
    setUploading(false);
  };

  const getVideoDuration = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.src = url;
    v.onloadedmetadata = () => { resolve(v.duration); URL.revokeObjectURL(url); };
    v.onerror = () => { resolve(0); URL.revokeObjectURL(url); };
  });

  // ── Upload WAV score ─────────────────────────────────────────────
  const handleAudioFile = async (file) => {
    setUploading(true);
    setRenderStatus("Uploading score...");
    try {
      const form = new FormData();
      form.append("session_id", sessionId);
      form.append("file", file);
      const r = await fetch(`${API}/episode-mixer/upload-clip`, { method: "POST", body: form });
      if (!r.ok) throw new Error(await r.text());
      const res = await r.json();
      setAudioFile({ name: res.filename, size: file.size });
      setAudioUploaded(true);
      setRenderStatus("✓ Score uploaded");
    } catch (e) {
      setRenderStatus("Audio upload failed: " + e.message);
    }
    setUploading(false);
  };

  // ── Remove a clip ────────────────────────────────────────────────
  const removeClip = (idx) => setClips(prev => prev.filter((_, i) => i !== idx));
  const moveClip = (idx, dir) => {
    setClips(prev => {
      const arr = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return arr;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return arr;
    });
  };

  // ── Start render ─────────────────────────────────────────────────
  const startRender = async () => {
    if (!clips.length) { setRenderStatus("Add at least one video clip first."); return; }
    setRenderStatus("Starting render...");
    setJobStatus("queued");
    setJobError(null);

    let offset = 0;
    const clipSpecs = clips.map(c => {
      const spec = {
        session_id: sessionId,
        clip_filename: c.name,
        start_sec: offset,
        duration_sec: c.duration || 10,
        use_embedded_audio: !audioFile,
      };
      offset += c.duration || 10;
      return spec;
    });

    const body = {
      session_id: sessionId,
      clips: clipSpecs,
      master_audio_filename: audioFile ? audioFile.name : null,
      output_name: (outputName.trim() || "episode_render") + ".mp4",
    };

    try {
      const r = await fetch(`${API}/episode-mixer/render`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await r.text());
      const res = await r.json();
      setJobId(res.job_id);
      setJobStatus("queued");
      setRenderStatus("Render queued — polling for status...");
      pollStatus(res.job_id);
    } catch (e) {
      setRenderStatus("Render failed: " + e.message);
      setJobStatus("failed");
    }
  };

  // ── Poll status ──────────────────────────────────────────────────
  const pollStatus = useCallback((jid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API}/episode-mixer/status/${jid}`);
        if (!r.ok) return;
        const res = await r.json();
        setJobStatus(res.status);
        if (res.status === "rendering") setRenderStatus("Rendering... FFmpeg is processing your clips.");
        if (res.status === "complete") {
          setRenderStatus("✓ Render complete — ready to download!");
          clearInterval(pollRef.current);
        }
        if (res.status === "failed") {
          setJobError(res.error);
          setRenderStatus("Render failed.");
          clearInterval(pollRef.current);
        }
      } catch {}
    }, 2000);
  }, []);

  // ── Download MP4 ─────────────────────────────────────────────────
  const downloadMp4 = () => {
    if (!jobId || jobStatus !== "complete") return;
    const a = document.createElement("a");
    a.href = `${API}/episode-mixer/download/${jobId}`;
    a.download = (outputName || "episode_render") + ".mp4";
    a.click();
  };

  // ── Drag and drop ────────────────────────────────────────────────
  const onDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("video/"));
    if (files.length) handleVideoFiles(files);
  };

  // ── Styles ───────────────────────────────────────────────────────
  const card = {
    background: "rgba(255,255,255,0.03)",
    border: `1px solid ${BORDER}`,
    borderRadius: "16px",
    padding: "20px",
    marginBottom: "16px",
  };

  const btn = (color, disabled) => ({
    padding: "10px 22px", borderRadius: "10px", border: "none",
    background: disabled ? "rgba(255,255,255,0.06)" : color,
    color: disabled ? MUTED : (color === GOLD ? NAVY : "#fff"),
    fontWeight: "700", fontSize: "14px", cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "inherit", transition: "opacity 0.2s",
    opacity: disabled ? 0.5 : 1,
  });

  const label = {
    fontSize: "11px", fontWeight: "700", color: MUTED,
    letterSpacing: "0.1em", textTransform: "uppercase", display: "block", marginBottom: "6px",
  };

  const totalDur = clips.reduce((s, c) => s + (c.duration || 0), 0);
  const canRender = clips.length > 0 && !uploading && jobStatus !== "rendering";
  const isComplete = jobStatus === "complete";

  return (
    <div style={{ padding: "24px", maxWidth: "860px", margin: "0 auto", fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#fff" }}>

      {/* Header */}
      <div style={{ marginBottom: "28px" }}>
        <div style={{ fontSize: "11px", fontWeight: "700", color: GOLD, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "6px" }}>MindStreams Mixer</div>
        <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "800", color: "#fff" }}>Video Board</h2>
        <p style={{ margin: "6px 0 0", fontSize: "13px", color: MUTED }}>
          Upload video clips + optional WAV score → render → download MP4
        </p>
      </div>

      {/* Session info */}
      <div style={{ ...card, display: "flex", gap: "24px", alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <span style={label}>Session ID</span>
          <span style={{ fontSize: "12px", fontFamily: "monospace", color: "rgba(255,255,255,0.5)" }}>{sessionId}</span>
        </div>
        <div>
          <span style={label}>Clips</span>
          <span style={{ fontSize: "18px", fontWeight: "800", color: "#fff" }}>{clips.length}</span>
        </div>
        <div>
          <span style={label}>Total Duration</span>
          <span style={{ fontSize: "18px", fontWeight: "800", color: GOLD }}>{totalDur.toFixed(1)}s</span>
        </div>
        <div>
          <span style={label}>Score</span>
          <span style={{ fontSize: "13px", color: audioUploaded ? GREEN : MUTED }}>
            {audioUploaded ? `✓ ${audioFile.name}` : "None (uses clip audio)"}
          </span>
        </div>
      </div>

      {/* Output name */}
      <div style={{ ...card }}>
        <label style={label}>Output Filename</label>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <input
            value={outputName}
            onChange={e => setOutputName(e.target.value)}
            placeholder="episode_render"
            style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: `1px solid ${BORDER}`, borderRadius: "8px", color: "#fff", padding: "10px 14px", fontSize: "14px", outline: "none", fontFamily: "inherit" }}
          />
          <span style={{ fontSize: "13px", color: MUTED }}>.mp4</span>
        </div>
      </div>

      {/* Video clip drop zone */}
      <div style={{ ...card }}
        onDragOver={e => e.preventDefault()}
        onDrop={onDrop}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
          <span style={label}>Video Clips</span>
          <button style={btn(INDIGO, uploading)} onClick={() => videoInputRef.current?.click()} disabled={uploading}>
            + Add Clips
          </button>
          <input ref={videoInputRef} type="file" accept="video/*" multiple style={{ display: "none" }}
            onChange={e => handleVideoFiles(e.target.files)} />
        </div>

        {clips.length === 0 ? (
          <div style={{ border: `2px dashed ${BORDER}`, borderRadius: "12px", padding: "40px", textAlign: "center", color: MUTED }}>
            <div style={{ fontSize: "32px", marginBottom: "10px" }}>🎬</div>
            <div style={{ fontSize: "14px" }}>Drag and drop MP4 clips here, or click Add Clips</div>
            <div style={{ fontSize: "12px", marginTop: "6px" }}>Clips will be concatenated in order</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {clips.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 14px", background: "rgba(255,255,255,0.03)", borderRadius: "10px", border: `1px solid ${BORDER}` }}>
                <span style={{ fontSize: "11px", fontWeight: "700", color: GOLD, minWidth: "24px" }}>{i + 1}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                  <div style={{ fontSize: "11px", color: MUTED }}>{c.duration ? c.duration.toFixed(2) + "s" : "—"}  ·  {fmt(c.size)}</div>
                </div>
                <div style={{ display: "flex", gap: "4px" }}>
                  <button onClick={() => moveClip(i, -1)} disabled={i === 0} style={{ ...btn("rgba(255,255,255,0.08)", i === 0), padding: "5px 9px", fontSize: "13px" }}>↑</button>
                  <button onClick={() => moveClip(i, 1)} disabled={i === clips.length - 1} style={{ ...btn("rgba(255,255,255,0.08)", i === clips.length - 1), padding: "5px 9px", fontSize: "13px" }}>↓</button>
                  <button onClick={() => removeClip(i)} style={{ ...btn(RED, false), padding: "5px 9px", fontSize: "13px" }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Score / WAV upload */}
      <div style={{ ...card }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", flexWrap: "wrap", gap: "10px" }}>
          <div>
            <span style={label}>Score / WAV Mix (Optional)</span>
            <span style={{ fontSize: "12px", color: MUTED }}>Upload the WAV exported from the Soundboard to replace clip audio</span>
          </div>
          <button style={btn(audioUploaded ? GREEN : "rgba(255,255,255,0.1)", uploading)} onClick={() => audioInputRef.current?.click()} disabled={uploading}>
            {audioUploaded ? "✓ Score Loaded" : "↑ Upload WAV Score"}
          </button>
          <input ref={audioInputRef} type="file" accept="audio/wav,audio/mp3,audio/*" style={{ display: "none" }}
            onChange={e => e.target.files[0] && handleAudioFile(e.target.files[0])} />
        </div>
        {audioUploaded && (
          <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 14px", background: "rgba(5,150,105,0.08)", borderRadius: "8px", border: `1px solid rgba(5,150,105,0.3)` }}>
            <span style={{ fontSize: "18px" }}>🎵</span>
            <div>
              <div style={{ fontSize: "13px", fontWeight: "600", color: GREEN }}>{audioFile.name}</div>
              <div style={{ fontSize: "11px", color: MUTED }}>{fmt(audioFile.size)} — will replace all clip audio tracks</div>
            </div>
            <button onClick={() => { setAudioFile(null); setAudioUploaded(false); }} style={{ ...btn(RED, false), padding: "4px 10px", fontSize: "12px", marginLeft: "auto" }}>Remove</button>
          </div>
        )}
      </div>

      {/* Render controls */}
      <div style={{ ...card, background: "rgba(79,70,229,0.05)", borderColor: "rgba(79,70,229,0.2)" }}>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <button style={{ ...btn(GOLD, !canRender), fontSize: "15px", padding: "13px 32px" }}
            onClick={startRender} disabled={!canRender}>
            {jobStatus === "rendering" ? "⏳ Rendering..." : "▶ Render MP4"}
          </button>

          {isComplete && (
            <button style={{ ...btn(GREEN, false), fontSize: "15px", padding: "13px 32px" }}
              onClick={downloadMp4}>
              ↓ Download MP4
            </button>
          )}

          {renderStatus && (
            <span style={{ fontSize: "13px", color: renderStatus.startsWith("✓") ? GREEN : renderStatus.startsWith("Render failed") ? RED : MUTED }}>
              {renderStatus}
            </span>
          )}
        </div>

        {/* Progress indicator */}
        {(jobStatus === "queued" || jobStatus === "rendering") && (
          <div style={{ marginTop: "14px" }}>
            <div style={{ height: "4px", background: "rgba(255,255,255,0.08)", borderRadius: "2px", overflow: "hidden" }}>
              <div style={{ height: "100%", background: GOLD, borderRadius: "2px", width: jobStatus === "rendering" ? "60%" : "20%", transition: "width 1s ease", animation: "pulse 2s infinite" }} />
            </div>
            <div style={{ fontSize: "11px", color: MUTED, marginTop: "6px" }}>
              {jobStatus === "queued" ? "Job queued — FFmpeg starting up..." : "FFmpeg processing — this may take a minute for longer episodes"}
            </div>
          </div>
        )}

        {jobError && (
          <div style={{ marginTop: "12px", padding: "10px 14px", background: "rgba(239,68,68,0.08)", borderRadius: "8px", border: `1px solid rgba(239,68,68,0.3)`, fontSize: "12px", color: "#f87171", fontFamily: "monospace" }}>
            {jobError.slice(0, 400)}
          </div>
        )}

        <div style={{ marginTop: "12px", fontSize: "12px", color: MUTED, lineHeight: 1.6 }}>
          Clips will be concatenated in listed order. If a WAV score is loaded it will replace all clip audio. Output is H.264 MP4 with AAC audio.
        </div>
      </div>

      {/* Job history */}
      {jobId && (
        <div style={{ ...card }}>
          <span style={label}>Current Job</span>
          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", fontSize: "13px" }}>
            <span><span style={{ color: MUTED }}>ID: </span><span style={{ fontFamily: "monospace", fontSize: "11px" }}>{jobId}</span></span>
            <span><span style={{ color: MUTED }}>Status: </span>
              <span style={{ color: isComplete ? GREEN : jobStatus === "failed" ? RED : GOLD, fontWeight: "700", textTransform: "uppercase" }}>
                {jobStatus}
              </span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
